import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge, Stars, Spinner, EmptyState } from "@/components/Common";
import {
  Building2, Search, Send, Download, CheckCircle2, MessageSquareReply,
  Mail, MapPin, X, Filter,
} from "lucide-react";
import { toast } from "sonner";

function SendModal({ lead, onClose, onSent }) {
  const [settings, setSettings] = useState(null);
  const [cvs, setCvs] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [cvId, setCvId] = useState("");
  const [sending, setSending] = useState(false);

  const render = (t) => (t || "")
    .replaceAll("{NOME_HOTEL}", lead.hotel_name)
    .replaceAll("{CITTA}", lead.city)
    .replaceAll("{RUOLO_CANDIDATURA}", (lead.roles || ["Koch"])[0]);

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([api.get("/settings"), api.get("/cv")]);
      setSettings(s.data);
      setCvs(c.data);
      setSubject(render(s.data.subject_template));
      setMessage(render(s.data.message_template));
      const primary = c.data.find((x) => x.is_primary);
      setCvId(primary?.id || c.data[0]?.id || "");
    })();
    // eslint-disable-next-line
  }, []);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post("/emails/send", { lead_id: lead.id, subject, message, cv_id: cvId || null });
      if (res.data.status === "simulated")
        toast.warning("Email simulata (nessuna API key attiva)", { description: lead.email });
      else toast.success("Candidatura inviata!", { description: `${lead.hotel_name} → ${lead.email}` });
      onSent();
      onClose();
    } catch (e) {
      toast.error("Invio fallito", { description: e?.response?.data?.detail || "Errore Resend" });
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="send-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-[#0E1015] border-l border-white/10 overflow-y-auto fade-up">
        <div className="sticky top-0 bg-[#0E1015]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-amber-400" />
            <h3 className="font-head font-semibold">Invia Candidatura</h3>
          </div>
          <button onClick={onClose} data-testid="close-send-modal" className="text-gray-500 hover:text-gray-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="card p-4 border-amber-500/20">
            <div className="font-head font-semibold text-gray-100">{lead.hotel_name}</div>
            <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{lead.city}</span>
              <Stars count={lead.stars} />
            </div>
            <div className="flex items-center gap-1.5 text-sm text-amber-400 mt-2 font-mono">
              <Mail className="w-3.5 h-3.5" />{lead.email}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Oggetto</label>
            <input data-testid="send-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Messaggio (CV allegato automaticamente)</label>
            <textarea data-testid="send-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={9}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm font-mono leading-relaxed" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">CV Allegato</label>
            <select data-testid="send-cv-select" value={cvId} onChange={(e) => setCvId(e.target.value)}
              className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm">
              {cvs.length === 0 && <option value="">Nessun CV caricato</option>}
              {cvs.map((c) => <option key={c.id} value={c.id}>{c.filename}{c.is_primary ? " (principale)" : ""}</option>)}
            </select>
            {cvs.length === 0 && <p className="text-xs text-red-400 mt-1.5">Carica un CV nella sezione "Gestione CV" per allegarlo.</p>}
          </div>
          <button data-testid="confirm-send-btn" onClick={send} disabled={sending}
            className="btn-copper w-full py-3 rounded-lg text-sm flex items-center justify-center gap-2">
            {sending ? <Spinner /> : <Send className="w-4 h-4" />}
            {sending ? "Invio in corso..." : "Invia Candidatura con CV"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [hiringOnly, setHiringOnly] = useState(false);
  const [modalLead, setModalLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (hiringOnly) params.set("hiring", "true");
    const res = await api.get(`/leads?${params.toString()}`);
    setLeads(res.data);
    setLoading(false);
  }, [search, status, hiringOnly]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const mark = async (lead, kind) => {
    await api.post(`/leads/${lead.id}/${kind}`);
    toast.success(kind === "mark-reply" ? "Segnato come 'Risposta'" : "Segnato come 'Inviata'");
    load();
  };

  const exportCsv = () => {
    const rows = [["Hotel", "Email", "Città", "Regione", "Stelle", "Stato", "Assume"],
      ...leads.map((l) => [l.hotel_name, l.email, l.city, l.region, l.stars, l.status, l.hiring ? "Sì" : "No"])];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "hotel_email_germania.csv";
    a.click();
    toast.success("Esportato CSV", { description: `${leads.length} record` });
  };

  return (
    <div className="p-6 max-w-[1500px]">
      <PageHeader
        title="Database Hotel & Email Trovate"
        subtitle="Email reali estratte dalle pagine Impressum/Kontakt degli hotel tedeschi"
        icon={Building2}
        actions={
          <button data-testid="export-csv-btn" onClick={exportCsv}
            className="px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 bg-white/5 border border-white/10 hover:border-amber-500/30 text-gray-200">
            <Download className="w-4 h-4" /> Esporta CSV
          </button>
        }
      />

      <div className="card p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="leads-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca hotel, email o città..."
            className="input-dark w-full pl-9 pr-3 py-2.5 text-sm" />
        </div>
        <select data-testid="leads-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="input-dark px-3 py-2.5 text-sm">
          <option value="">Tutti gli stati</option>
          <option value="non_inviata">Non inviata</option>
          <option value="inviata">Inviata</option>
          <option value="risposta">Risposta</option>
        </select>
        <button data-testid="hiring-filter" onClick={() => setHiringOnly((v) => !v)}
          className={`px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 border transition-all ${
            hiringOnly ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-white/5 text-gray-300 border-white/10"
          }`}>
          <Filter className="w-4 h-4" /> Solo chi assume
        </button>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><Spinner className="w-7 h-7" /></div>
        ) : leads.length === 0 ? (
          <EmptyState icon={Building2} title="Nessuna email ancora"
            hint="Lo scraper sta cercando o non ha trovato risultati. Avvia manualmente dalla Panoramica." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <th className="px-4 py-3">Hotel</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Città</th>
                  <th className="px-4 py-3">Ruoli rilevati</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-100">{l.hotel_name}</div>
                      <div className="flex items-center gap-2 mt-0.5"><Stars count={l.stars} />
                        {l.hiring && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Assume</span>}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400 border border-white/10">{l.source}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-amber-400/90 text-xs">{l.email}</td>
                    <td className="px-4 py-3 text-gray-300">{l.city}<div className="text-[11px] text-gray-500">{l.region}</div></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(l.roles || []).length === 0 ? <span className="text-gray-600 text-xs">—</span> :
                          l.roles.map((r) => <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-gray-300">{r}</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`send-lead-${l.id}`} onClick={() => setModalLead(l)} title="Invia candidatura"
                          className="p-2 rounded-lg bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 transition-colors"><Send className="w-4 h-4" /></button>
                        <button data-testid={`mark-sent-${l.id}`} onClick={() => mark(l, "mark-sent")} title="Segna inviata"
                          className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-sky-400 transition-colors"><CheckCircle2 className="w-4 h-4" /></button>
                        <button data-testid={`mark-reply-${l.id}`} onClick={() => mark(l, "mark-reply")} title="Segna risposta"
                          className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-emerald-400 transition-colors"><MessageSquareReply className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {leads.length > 0 && <div className="text-xs text-gray-500 mt-3">{leads.length} email hotel nel database</div>}

      {modalLead && <SendModal lead={modalLead} onClose={() => setModalLead(null)} onSent={load} />}
    </div>
  );
}
